import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../AuthContext';
import { Logo } from '../components/Brand';
import { ThemeToggle } from '../components/ThemeToggle';
import { toast } from 'sonner';

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: '', email: '', password: '', phone: '' });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...form, role: 'CLIENT' });
      toast.success('Account created. Welcome to JFK9!');
      navigate('/client', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <div className='min-h-screen min-h-[100dvh] grid md:grid-cols-2 bg-[#FFF8F2] dark:bg-slate-950'>
      <div className='hidden md:flex flex-col justify-between p-12 bg-[#0B1220] text-white relative overflow-hidden'>
        <Logo />
        <div className='relative z-10'>
          <h1 className='font-display text-4xl font-bold tracking-tight'>Join the pack.</h1>
          <p className='text-slate-300 mt-4 max-w-md'>Create your pet parent account in 30 seconds. Upload dog profiles + VAX records, then book any of our 15 boarding runs.</p>
        </div>
        <div className='relative z-10 text-xs text-slate-500'>© {new Date().getFullYear()} K9os.app</div>
        <div className='absolute -right-20 -bottom-20 w-[400px] h-[400px] rounded-full bg-[#C8102E]/10 blur-3xl' />
      </div>
      <div className='flex flex-col min-h-screen min-h-[100dvh] md:min-h-0'>
        <div className='md:hidden flex items-center justify-between px-4 pt-3 pb-2'>
          <Link to='/' aria-label='Home' className='flex items-center'><Logo /></Link>
          <ThemeToggle size='sm' />
        </div>
        <div className='flex-1 flex items-center justify-center px-4 py-6 sm:px-6 md:p-12'>
          <Card className='w-full max-w-md mx-auto p-5 sm:p-7 md:p-8 shadow-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900'>
            <h2 className='font-display text-2xl sm:text-3xl font-bold text-[#0B1220] dark:text-slate-50'>Create your account</h2>
            <p className='text-sm text-slate-500 dark:text-slate-400 mt-1 mb-5'>Pet parents get instant access.</p>
            <form onSubmit={submit} className='space-y-4' autoComplete='on'>
              <div>
                <Label htmlFor='full_name'>Full name</Label>
                <Input id='full_name' autoComplete='name' required value={form.full_name} onChange={(e)=>setForm({...form, full_name: e.target.value})} placeholder='Jane Doe' className='h-12 text-base sm:h-11 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100' data-testid='register-name-input' />
              </div>
              <div>
                <Label htmlFor='email'>Email</Label>
                <Input id='email' type='email' autoComplete='email' inputMode='email' required value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} placeholder='you@example.com' className='h-12 text-base sm:h-11 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100' data-testid='register-email-input' />
              </div>
              <div>
                <Label htmlFor='phone'>Phone</Label>
                <Input id='phone' type='tel' autoComplete='tel' inputMode='tel' value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} placeholder='(555) 123-4567' className='h-12 text-base sm:h-11 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100' data-testid='register-phone-input' />
              </div>
              <div>
                <Label htmlFor='password'>Password</Label>
                <Input id='password' type='password' autoComplete='new-password' required value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} placeholder='At least 8 characters' className='h-12 text-base sm:h-11 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100' data-testid='register-password-input' />
              </div>
              <Button type='submit' className='w-full h-12 text-base sm:h-11 sm:text-sm bg-[#1B3A87] hover:bg-[#16306F] text-white' disabled={loading} data-testid='register-submit-button'>
                {loading ? 'Creating…' : 'Create account'}
              </Button>
            </form>
            <div className='mt-5 text-sm text-slate-600 dark:text-slate-300 text-center'>
              Already have an account?{' '}
              <Link to='/login' className='text-[#1B3A87] dark:text-[#93C5FD] font-semibold hover:underline' data-testid='register-login-link'>Sign in</Link>
            </div>
          </Card>
        </div>
        <div className='md:hidden text-center text-[11px] text-slate-400 pb-4 px-4'>
          © {new Date().getFullYear()} K9os.app
        </div>
      </div>
    </div>
  );
}
